import axios from "axios";
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { useAuth } from "../components/AuthProvider";
import { userData } from "../recoil/atom/userData";
import { API_DOMAIN } from "../utils/constants";
import { impersonateData } from "../recoil/atom/impersonate";
import { classifyLead } from "../utils/classifyLead";
import { useFormik } from "formik";
import { RemarkValidation } from "../utils/validations";

export const useGetLeadsData = () => {
    // Hooks for user context
    const user = useRecoilValue(userData);
    const impersonate = useRecoilValue(impersonateData);
    const actualUser = impersonate?.impersonate ? impersonate.businessDetails : user;
    const { logout } = useAuth();
    const navigate = useNavigate();

    // Data states
    const [leadsData, setLeadsData] = useState([]);
    const [noFeedback, setNoFeedback] = useState([]);
    const [failed, setFailed] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);

    // Remarks Modal Form
    const addRemark = async (values) => {
        setButtonLoading(true);
        try { }
        catch (err) {
            console.error(err);
        }
        finally {
            setButtonLoading(false);
            toggleRemarkModal(false);
        }
    }

    const editLeadRemark = (lead) => {
        setFieldValue("leadId", lead.id);
        toggleRemarkModal(true)
    }

    const closeModal = () => {
        resetForm()
        toggleRemarkModal(false);
    }
    const [remarkModal, toggleRemarkModal] = useState(false);
    const [buttonLoading, setButtonLoading] = useState(false);
    const { errors, handleChange, setFieldValue, handleSubmit, resetForm } = useFormik({
        initialValues: {
            remark: "",
            id: "",
        },
        onSubmit: addRemark,
        validationSchema: RemarkValidation
    })


    // Fetch logic
    const fetchLeadsData = async () => {
        try {
            const isImpersonating = sessionStorage.getItem("impersonate") === "true" && sessionStorage.getItem("business");
            const endpointContext = isImpersonating ? `impersonate` : "nps-details";
            const token = isImpersonating ? sessionStorage.getItem("token") : localStorage.getItem("token");
            const response = await axios.get(`${API_DOMAIN}/${endpointContext}`, {
                params: isImpersonating
                    ? { businessId: sessionStorage.getItem("business"), feedbackType: "digital_lead" }
                    : { feedbackType: "digital_lead" },
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setLeadsData(response.data?.feedback ?? []);
            setNoFeedback(response.data?.noFeedback ?? []);
            setFailed(response.data?.failed ?? []);
        } catch (err) {
            console.error(err);
            logout(navigate("/"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLeadsData();
    }, []);

    // All derived calculations here:


    // ... in useGetLeadsData
    const dashboard = useMemo(() => {
        // Combine leadsData and noFeedback for calculations
        // noFeedback are always "Junk"
        const allLeads = [
            ...leadsData,
            ...noFeedback.map(nf => ({ ...nf, feedback: {}, classifiedStatus: "Junk" }))
        ];

        let statusCount = { Hot: 0, Warm: 0, Cold: 0, Junk: 0 };
        let sourceMap = {};
        let weekMap = {};
        let testDriveInterested = 0, testDriveBooked = 0;

        for (const lead of allLeads) {
            const src = lead.metadata.source;
            // If lead is from noFeedback, status is "Junk"
            // Otherwise use classifyLead
            const status = lead.classifiedStatus || classifyLead(lead.feedback);
            if (!sourceMap[src]) sourceMap[src] = { Hot: 0, Warm: 0, Cold: 0, Junk: 0 };
            sourceMap[src][status]++;
            statusCount[status]++;

            if (lead.feedback && (lead.feedback.test_drive === "yes" || lead.feedback.test_drive === "maybe"))
                testDriveInterested++;
            if (lead.feedback && lead.feedback.test_drive_date) testDriveBooked++;

            // Week calculation (use leadsData only for week breakdowns)
            if (lead.metadata?.createdTime) {
                const createdDate = new Date(lead.metadata.createdTime);
                const week = `${createdDate.getFullYear()}-W${Math.ceil(createdDate.getDate() / 7)}`;
                weekMap[week] = (weekMap[week] || 0) + 1;
            }
        }

        const barData = Object.entries(sourceMap).map(([src, stat]) => ({
            source: src.charAt(0).toUpperCase() + src.slice(1),
            ...stat,
        }));

        const barDataPercent = Object.entries(sourceMap).map(([src, stat]) => {
            const total = Object.values(stat).reduce((a, b) => a + b, 0) || 1; // avoid /0
            return {
                source: src.charAt(0).toUpperCase() + src.slice(1),
                Hot: Math.round((stat.Hot / total) * 100),
                Warm: Math.round((stat.Warm / total) * 100),
                Cold: Math.round((stat.Cold / total) * 100),
                Junk: Math.round((stat.Junk / total) * 100),
            };
        });

        const weekData = Object.keys(weekMap)
            .sort()
            .map((week, idx) => ({
                week: `Week ${idx + 1}`,
                leads: weekMap[week],
            }));

        const totalLeadsGenerated = allLeads.length;
        const totalLeadsGeneratedActual = statusCount.Hot + statusCount.Warm + statusCount.Cold;

        const totalMessagesSent = allLeads.reduce(
            (sum, lead) => sum + (lead.messageSent ? 1 : 0),
            0
        );
        const totalResponses = leadsData.length; // If you want to exclude noFeedback
        const leadGenerationRatio = totalMessagesSent
            ? `${Math.round((totalLeadsGenerated / totalMessagesSent) * 100)}%`
            : "0%";

        const kpi = [
            { label: "Total Leads Generated", value: totalLeadsGeneratedActual },
            { label: "Total Messages Sent", value: totalMessagesSent },
            { label: "Total Responses", value: totalResponses },
            { label: "Lead Generation Ratio", value: leadGenerationRatio },
            {
                label: "Response Ratio",
                value: `${Math.round((totalResponses / totalMessagesSent) * 100)}%`,
            },
            { label: "Test Drive Interested", value: testDriveInterested },
            { label: "Test Drive Booked", value: testDriveBooked },
        ];

        // Test Drive chart: use original leadsData (not noFeedback, as those do not have feedback)
        const sources = [...new Set(allLeads.map((l) => l.metadata.source))];
        const testDriveBarData = sources.map((source) => {
            const filtered = leadsData.filter((l) => l.metadata.source === source);
            return {
                source: source.charAt(0).toUpperCase() + source.slice(1),
                Interested: filtered.filter(
                    (l) => l.feedback.test_drive === "yes" || l.feedback.test_drive === "maybe"
                ).length,
                Booked: filtered.filter((l) => l.feedback.test_drive_date).length,
            };
        });

        // Best source card, also computed only from leads with feedback
        let best = null, bestScore = -1, bestHotWarm = -1, bestTestDrive = -1;
        const summaryArr = sources.map((source) => {
            const filtered = leadsData.filter((l) => l.metadata.source === source);
            const hotWarm = filtered.filter((l) => {
                const status = classifyLead(l.feedback);
                return status === "Hot" || status === "Warm";
            }).length;
            const testDriveRequested = filtered.filter((l) => l.feedback.test_drive === "yes").length;
            const score = hotWarm + testDriveRequested;
            if (score > bestScore) {
                best = source;
                bestScore = score;
                bestHotWarm = hotWarm;
                bestTestDrive = testDriveRequested;
            }
            return {
                source: source.charAt(0).toUpperCase() + source.slice(1),
                hotWarm,
                testDriveRequested,
                score,
            };
        });
        const bestSourceData = {
            bestSource: best ? best.charAt(0).toUpperCase() + best.slice(1) : "",
            bestHotWarm,
            bestTestDrive,
            bestScore,
            summaryArr,
        };

        return {
            kpi,
            barData,
            statusCount,
            weekData,
            testDriveStats: { testDriveInterested, testDriveBooked },
            barDataPercent,
            testDriveBarData,
            bestSourceData,
        };
    }, [leadsData, noFeedback]);


    // Return everything needed for the dashboard
    return {
        leads: leadsData,
        loading,
        noFeedback,
        failed,
        page,
        setPage,
        remarkModal,
        editLeadRemark,
        buttonLoading,
        setButtonLoading,
        errors,
        handleChange,
        handleSubmit,
        closeModal,
        ...dashboard,
    };
};
